import React from 'react';
import type { FinancialSummary } from '../../api/modules/subscriptions';
import { formatCents, formatDate, formatUtcDate } from './utils';

interface FinancialSummaryViewProps {
  summary: FinancialSummary;
}

export const FinancialSummaryView: React.FC<FinancialSummaryViewProps> = ({ summary }) => {
  const nextPayout = summary.payouts.next;
  const stripeMissing = summary.collected.thisMonthCents == null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <MoneyCard
          label="Collected this month"
          amountCents={summary.collected.thisMonthCents}
          detail="Paid subscription invoices"
        />
        <MoneyCard
          label="Net this month"
          amountCents={summary.netThisMonthCents}
          detail="Charges and refunds after Stripe fees"
        />
        <MoneyCard
          label="Available balance"
          amountCents={summary.balance.availableCents}
          detail="Available to pay out"
        />
        <MoneyCard
          label="Pending"
          amountCents={summary.balance.pendingCents}
          detail="Not yet available"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <CountCard label="Users" value={summary.users} detail="Accounts" />
        <CountCard
          label="Active subscriptions"
          value={summary.subscriptions.active}
          detail="Paying now"
        />
        <CountCard
          label="Trialing"
          value={summary.subscriptions.trialing}
          detail="Not billed yet"
        />
        <CountCard label="Past due" value={summary.subscriptions.pastDue} detail="Payment failed" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <MoneyCard
          label="Marshmallow packs this month"
          amountCents={summary.marshmallowsThisMonthCents}
          detail="One-time pack sales"
        />
        <MoneyCard
          label="Collected all time"
          amountCents={summary.collected.allTimeCents}
          detail="Paid subscription invoices"
        />
        <MoneyCard
          label="Paid out"
          amountCents={summary.payouts.paidCents}
          detail={
            nextPayout
              ? `Next payout ${formatCents(nextPayout.amountCents)} on ${formatUtcDate(nextPayout.arrivalDate)}`
              : 'No pending payout'
          }
        />
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent subscription payments</h3>
        </div>
        {stripeMissing ? (
          <p className="px-6 py-8 text-sm text-gray-500">Stripe data unavailable.</p>
        ) : summary.recentPayments.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-500">No subscription payments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summary.recentPayments.map(payment => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.customer}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {payment.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCents(payment.amountCents)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(payment.created)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

interface MoneyCardProps {
  label: string;
  amountCents: number | null;
  detail: string;
}

const MoneyCard: React.FC<MoneyCardProps> = ({ label, amountCents, detail }) => (
  <div className="card p-6">
    <p className="text-sm font-medium text-gray-600">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-2">
      {amountCents == null ? 'Unavailable' : formatCents(amountCents)}
    </p>
    <p className="text-xs text-gray-500 mt-1">
      {amountCents == null ? 'Stripe data unavailable' : detail}
    </p>
  </div>
);

interface CountCardProps {
  label: string;
  value: number | null;
  detail: string;
}

const CountCard: React.FC<CountCardProps> = ({ label, value, detail }) => (
  <div className="card p-6">
    <p className="text-sm font-medium text-gray-600">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-2">{value == null ? 'Unavailable' : value}</p>
    <p className="text-xs text-gray-500 mt-1">{detail}</p>
  </div>
);
